/**
 * File: booksSetupUtils.ts
 * Path: apps/admin/src/features/accounting/utils/booksSetupUtils.ts
 * Description: Plain helpers behind the books Setup page: the setup steps
 * worked out from the books themselves (and the saved Compare with Zoho
 * month ends), which tabs the page has, the
 * latest month the books may start from, and the change log's time stamp.
 */

import { formatContractDate, parallelRunProgress, ZOHO_CHECK_LIMITS, type BooksProfile, type Journal, type ZohoCheckRecord } from "@idk/config";
import { monthName } from "@/shared/utils/booksFormatUtils";

export type SetupTab = "books" | "opening" | "import" | "compare" | "lock";

export interface SetupStep {
  id: string;
  title: string;
  detail: string;
  done: boolean;
}

/** A change-log time: day and time in South African time; empty when there is no time. */
export const formatWhen = (ms: number | null) =>
  ms === null
    ? ""
    : new Intl.DateTimeFormat("en-ZA", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Johannesburg" }).format(
        new Date(ms),
      );

/** Compare with Zoho's step: how far the parallel run is. */
const compareStep = (checks: readonly Pick<ZohoCheckRecord, "monthEnd" | "matching" | "events">[]): SetupStep => {
  const run = parallelRunProgress(checks);
  return {
    id: "compare",
    title: "Compare with Zoho",
    detail: run.done
      ? "Zoho can go read-only"
      : checks.length === 0
        ? "No month end compared yet"
        : `${run.matching.length} of ${ZOHO_CHECK_LIMITS.needed} month ends match`,
    done: run.done,
  };
};

/** The setup steps (six for the company, three for personal books), worked out from the books themselves. */
export const setupSteps = (
  profile: BooksProfile,
  journals: readonly Journal[],
  checks: readonly Pick<ZohoCheckRecord, "monthEnd" | "matching" | "events">[] = [],
): SetupStep[] => {
  const live = journals.filter((j) => !j.reversedBy);
  const opening = live.filter((j) => j.source === "opening").length;
  const imported = live.filter((j) => j.source === "import");
  const unlinked = imported.filter((j) => j.party && !j.clientId).length;
  const steps: SetupStep[] = [
    { id: "started", title: "Books started", detail: `${formatContractDate(profile.booksStart)}, year ends ${monthName(profile.yearEndMonth)}`, done: true },
    { id: "opening", title: "Opening balances", detail: opening > 0 ? "Posted to ledger" : "Not posted yet", done: opening > 0 },
  ];
  if (profile.kind === "company") {
    steps.push(
      { id: "zoho", title: "Zoho history", detail: imported.length > 0 ? `${imported.length} entries imported` : "Not imported yet", done: imported.length > 0 },
      { id: "link", title: "Clients linked", detail: imported.length === 0 ? "After the import" : unlinked > 0 ? `${unlinked} entries still unlinked` : "All linked", done: imported.length > 0 && unlinked === 0 },
      compareStep(checks),
    );
  }
  steps.push({
    id: "lock",
    title: "Month locked",
    detail: profile.lockedThrough ? `Through ${formatContractDate(profile.lockedThrough)}` : "Nothing locked yet",
    done: profile.lockedThrough !== null,
  });
  return steps;
};

/** The Setup tabs. Personal books never ran in Zoho, so they get three. */
export const setupTabs = (profile: Pick<BooksProfile, "kind">): { tab: SetupTab; label: string }[] => [
  { tab: "books", label: "Books" },
  { tab: "opening", label: "Opening balances" },
  ...(profile.kind === "company"
    ? [
        { tab: "import" as const, label: "Import from Zoho" },
        { tab: "compare" as const, label: "Compare with Zoho" },
      ]
    : []),
  { tab: "lock", label: "Lock months" },
];

/** The first of the month before the books' first day, as YYYY-MM. */
export const monthBefore = (booksStart: string) => {
  const [y, m] = booksStart.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
};
